import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import { apiRequest } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { withConcurrency } from "@/lib/concurrency";
import { uploadFileWithProgress } from "@/lib/upload-with-progress";
import { propertyPlaceholderImageUrl } from "@/features/property/utils/placeholder-image";
import type { components } from "@/types/api";
import type {
  PageResponse,
  Property,
  PropertyImage,
  PropertyImageWire,
  PropertyListItemWire,
  PropertyListQuery,
  PropertyStatus,
  PropertyWire,
} from "@/types/property";

// ---------------------------------------------------------------------------
// Wire → view-model converters
//
// The codegen emits every scalar as optional (`bedrooms?: number`) because
// springdoc marks records that way at the OpenAPI layer. The view-model
// `Property` interface promises `bedrooms: number | null` (and similar)
// for ergonomic rendering — but without an explicit conversion step,
// `undefined` leaks through under the view-model's typed contract and
// renders as the literal "undefined" in JSX or breaks `!== null` guards.
// These converters normalise `undefined → null` for every nullable field
// at the API boundary so the view-model's contract holds at runtime.
//
// `wire.field!` is intentional for fields the view-model declares as
// non-null required: a missing required field is a backend bug we want
// surfaced as an obvious TypeError (the only fix is on the backend).
// ---------------------------------------------------------------------------

export function toProperty(wire: PropertyWire): Property {
  return {
    id: wire.id!,
    listingType: wire.listingType!,
    wardCode: wire.wardCode!,
    wardDisplayName: wire.wardDisplayName ?? wire.wardCode ?? "",
    buildingName: wire.buildingName ?? null,
    projectId: wire.projectId ?? null,
    floor: wire.floor ?? null,
    axis: wire.axis ?? null,
    floorBand: wire.floorBand ?? null,
    area: wire.area!,
    bedrooms: wire.bedrooms ?? null,
    bathrooms: wire.bathrooms ?? null,
    price: wire.price!,
    status: wire.status!,
    assignedBrokerId: wire.assignedBrokerId ?? null,
    assignedBrokerName: wire.assignedBrokerName ?? null,
    assignedBrokerPhoneNumber: wire.assignedBrokerPhoneNumber ?? null,
    internalCode: wire.internalCode ?? null,
    description: wire.description ?? null,
    propertyType: wire.propertyType ?? null,
    furniture: wire.furniture ?? null,
    brokerageFeeType: wire.brokerageFeeType ?? null,
    brokerageFeeValue: wire.brokerageFeeValue ?? null,
    // Image-less properties fall back to a seeded placeholder (never stored;
    // a real upload replaces it) so cards don't render an empty gray box.
    thumbnailUrl: wire.thumbnailUrl ?? propertyPlaceholderImageUrl(wire.id!),
    imageCount: wire.imageCount ?? 0,
    lastFreshnessConfirmedAt: wire.lastFreshnessConfirmedAt ?? null,
    createdAt: wire.createdAt!,
    updatedAt: wire.updatedAt!,
  };
}

/**
 * The backend's PropertyListItemResponse is a subset of PropertyResponse
 * (no `description`, `assignedBrokerId`, `createdAt`); the view-model
 * layer currently uses `Property` for both detail and list, with the
 * missing fields defaulting to null/0. Splitting into a dedicated list
 * view-model is a future batch.
 *
 * `lastFreshnessConfirmedAt` IS included so the list-page card badge
 * can colour itself correctly without a follow-up detail fetch.
 */
export function toPropertyListItem(wire: PropertyListItemWire): Property {
  return {
    id: wire.id!,
    listingType: wire.listingType!,
    wardCode: wire.wardCode!,
    wardDisplayName: wire.wardDisplayName ?? wire.wardCode ?? "",
    buildingName: wire.buildingName ?? null,
    // The list projection omits the project link; the detail fetch carries it.
    projectId: null,
    floor: wire.floor ?? null,
    // Axis is detail-only (cards don't show it), like description.
    axis: null,
    floorBand: wire.floorBand ?? null,
    area: wire.area!,
    bedrooms: wire.bedrooms ?? null,
    bathrooms: wire.bathrooms ?? null,
    price: wire.price!,
    status: wire.status!,
    assignedBrokerId: null,
    internalCode: wire.internalCode ?? null,
    description: null,
    // The list projection now carries floor / furniture / brokerage fee so the
    // card can show the same spec tiles as the detail; propertyType stays
    // detail-only.
    propertyType: null,
    furniture: wire.furniture ?? null,
    brokerageFeeType: wire.brokerageFeeType ?? null,
    brokerageFeeValue: wire.brokerageFeeValue ?? null,
    // Image-less properties fall back to a seeded placeholder (never stored;
    // a real upload replaces it) so cards don't render an empty gray box.
    thumbnailUrl: wire.thumbnailUrl ?? propertyPlaceholderImageUrl(wire.id!),
    imageCount: wire.imageCount ?? 0,
    lastFreshnessConfirmedAt: wire.lastFreshnessConfirmedAt ?? null,
    createdAt: wire.updatedAt!,
    updatedAt: wire.updatedAt!,
  };
}

export function toPropertyImage(wire: PropertyImageWire): PropertyImage {
  // The backend now always populates thumbnailUrl/fullUrl (falling back to
  // directUrl when the storage backend can't presign), but Jackson is
  // configured with NON_NULL serialization so an older deploy may still
  // omit them. Belt + suspenders: fall back to directUrl on the client too
  // so the gallery is never blank.
  const directUrl = wire.directUrl ?? "";
  return {
    id: wire.id!,
    propertyId: wire.propertyId!,
    originalFilename: wire.originalFilename ?? "",
    mimeType: wire.mimeType ?? "",
    sizeBytes: wire.sizeBytes ?? 0,
    displayOrder: wire.displayOrder ?? 0,
    watermarkApplied: wire.watermarkApplied ?? false,
    thumbnailUrl: wire.thumbnailUrl ?? directUrl,
    fullUrl: wire.fullUrl ?? directUrl,
    directUrl,
    createdAt: wire.createdAt!,
  };
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const propertyKeys = {
  all: ["properties"] as const,
  lists: () => [...propertyKeys.all, "list"] as const,
  list: (query: PropertyListQuery) => [...propertyKeys.lists(), query] as const,
  details: () => [...propertyKeys.all, "detail"] as const,
  detail: (id: number) => [...propertyKeys.details(), id] as const,
};

export const propertyImageKeys = {
  all: ["property-images"] as const,
  lists: () => [...propertyImageKeys.all, "list"] as const,
  list: (propertyId: number) => [...propertyImageKeys.lists(), propertyId] as const,
};

// ---------------------------------------------------------------------------
// Write payload types. Derived from the backend OpenAPI schema via codegen
// (`make dev-codegen`). Contract drift surfaces as a tsc error here.
// ---------------------------------------------------------------------------

export type PropertyCreateRequest = components["schemas"]["PropertyCreateRequest"];

/**
 * Generic patch shape covering the three "axes of change" the backend
 * exposes: info (PropertyUpdateInfoRequest), price (PropertyPriceChangeRequest),
 * and status (PropertyStatusChangeRequest). The frontend collapses these
 * into one PATCH for ergonomics; the union below keeps tsc happy.
 */
export type PropertyUpdateRequest =
  | components["schemas"]["PropertyUpdateInfoRequest"]
  | components["schemas"]["PropertyPriceChangeRequest"]
  | components["schemas"]["PropertyStatusChangeRequest"];

// ---------------------------------------------------------------------------
// Network calls
// ---------------------------------------------------------------------------

function buildPropertyListPath(query: PropertyListQuery): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.status !== undefined) params.set("status", query.status);
  if (query.wardCode !== undefined && query.wardCode !== "")
    params.set("wardCode", query.wardCode);
  if (query.search !== undefined && query.search !== "") params.set("search", query.search);
  if (query.building !== undefined && query.building !== "")
    params.set("building", query.building);
  if (query.projectId !== undefined)
    params.set("projectId", String(query.projectId));
  const qs = params.toString();
  return qs ? `/properties?${qs}` : "/properties";
}

async function fetchProperties(query: PropertyListQuery): Promise<PageResponse<Property>> {
  const page = await apiRequest<
    components["schemas"]["PageResponsePropertyListItemResponse"]
  >(buildPropertyListPath(query));
  return {
    ...page,
    items: (page.items ?? []).map(toPropertyListItem),
  };
}

async function fetchProperty(id: number): Promise<Property> {
  return toProperty(await apiRequest<PropertyWire>(`/properties/${id}`));
}

async function postProperty(body: PropertyCreateRequest): Promise<Property> {
  return toProperty(
    await apiRequest<PropertyWire>("/properties", { method: "POST", body }),
  );
}

async function patchProperty(id: number, body: PropertyUpdateRequest): Promise<Property> {
  return toProperty(
    await apiRequest<PropertyWire>(`/properties/${id}`, { method: "PATCH", body }),
  );
}

// Price and status have dedicated sub-resource endpoints because they
// each carry their own activity-log action code (`PRICE_CHANGED` /
// `STATUS_CHANGED`) with `old → new` values that the generic info-patch
// endpoint can't produce. The parent `/properties/{id}` route
// deserialises into `PropertyUpdateInfoRequest` which has no `price` or
// `status` field — Jackson would silently drop those keys, so we have
// to hit the dedicated routes for the change to actually take effect.
async function patchPropertyPrice(id: number, price: number): Promise<Property> {
  return toProperty(
    await apiRequest<PropertyWire>(`/properties/${id}/price`, {
      method: "PATCH",
      body: { price },
    }),
  );
}

async function patchPropertyStatus(
  id: number,
  status: PropertyStatus,
): Promise<Property> {
  return toProperty(
    await apiRequest<PropertyWire>(`/properties/${id}/status`, {
      method: "PATCH",
      body: { status },
    }),
  );
}

function deletePropertyApi(id: number) {
  return apiRequest<void>(`/properties/${id}`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Bulk import (wedge batch — Excel import flow)
// ---------------------------------------------------------------------------

export type BulkImportRowError = components["schemas"]["PropertyBulkImportError"];
export type BulkImportResponse = components["schemas"]["PropertyBulkImportResponse"];
export type BulkImportRequest = components["schemas"]["PropertyBulkImportRequest"];

function postBulkImport(body: BulkImportRequest): Promise<BulkImportResponse> {
  return apiRequest<BulkImportResponse, BulkImportRequest>(
    "/properties/bulk-import",
    { method: "POST", body },
  );
}

export function useBulkImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: PropertyCreateRequest[]) => postBulkImport({ items }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Cache invalidation helper. Used by every mutation to keep list queries
// consistent and warm the detail cache for the touched property.
// ---------------------------------------------------------------------------

function syncPropertyIntoCache(queryClient: QueryClient, property: Property): void {
  queryClient.setQueryData(propertyKeys.detail(property.id), property);
  void queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useProperties(query: PropertyListQuery) {
  return useQuery({
    queryKey: propertyKeys.list(query),
    queryFn: () => fetchProperties(query),
    placeholderData: (prev) => prev,
  });
}

export function useProperty(id: number | undefined) {
  return useQuery({
    queryKey: id !== undefined ? propertyKeys.detail(id) : propertyKeys.details(),
    queryFn: () => {
      if (id === undefined) throw new Error("useProperty called without an id");
      return fetchProperty(id);
    },
    enabled: id !== undefined,
  });
}

export function usePrefetchProperty(): (id: number) => void {
  const queryClient = useQueryClient();
  return (id: number) => {
    queryClient.prefetchQuery({
      queryKey: propertyKeys.detail(id),
      queryFn: () => fetchProperty(id),
    });
  };
}

export function useCreateProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertyCreateRequest) => postProperty(body),
    onSuccess: (property) => {
      syncPropertyIntoCache(queryClient, property);
    },
  });
}

/**
 * Patch the non-price / non-status fields of a property. The backend
 * splits the three "axes of change" into separate endpoints to keep
 * audit logs and authorisation clean; the frontend collapses them into
 * one form (the same PATCH) but exposes the hooks separately so each
 * call site reads naturally.
 */
export function useUpdateInfo(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PropertyUpdateRequest) => patchProperty(id, body),
    onSuccess: (property) => {
      syncPropertyIntoCache(queryClient, property);
    },
  });
}

export function useChangePrice(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (price: number) => patchPropertyPrice(id, price),
    onSuccess: (property) => {
      syncPropertyIntoCache(queryClient, property);
    },
  });
}

export function useChangeStatus(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (status: PropertyStatus) => patchPropertyStatus(id, status),
    onMutate: async (status) => {
      // Optimistic: paint the new status before the request resolves so
      // the badge feels instant. Roll back on error.
      await queryClient.cancelQueries({ queryKey: propertyKeys.detail(id) });
      const previous = queryClient.getQueryData<Property>(propertyKeys.detail(id));
      if (previous !== undefined) {
        queryClient.setQueryData<Property>(propertyKeys.detail(id), {
          ...previous,
          status,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(propertyKeys.detail(id), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: propertyKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
    },
  });
}

/**
 * Named alias for {@link useChangeStatus}. Property-detail page quick-status
 * picker uses this name to match the brief's API. Behavior is identical:
 * optimistic update against the detail cache + lists invalidation on settle.
 */
export const useChangePropertyStatus = useChangeStatus;

export function useDeleteProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePropertyApi(id),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: propertyKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: propertyKeys.lists() });
    },
  });
}

// ---------------------------------------------------------------------------
// Freshness confirmation (bucket 3 — owner/manager taps "Tin còn" to refresh
// `lastFreshnessConfirmedAt` so the property drops off the stale list).
// ---------------------------------------------------------------------------

async function confirmPropertyFresh(id: number): Promise<Property> {
  return toProperty(
    await apiRequest<PropertyWire>(`/properties/${id}/confirm-fresh`, {
      method: "PATCH",
    }),
  );
}

export function useConfirmPropertyFresh(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => confirmPropertyFresh(id),
    onSuccess: (property) => {
      syncPropertyIntoCache(queryClient, property);
    },
  });
}

// ---------------------------------------------------------------------------
// Property share tokens (bucket 3 — public-property page support).
// ---------------------------------------------------------------------------

export type ShareTokenResponse = components["schemas"]["PropertyShareTokenResponse"];

type IssueShareTokenInput = components["schemas"]["PropertyShareTokenIssueRequest"];

function postShareToken(id: number, body: IssueShareTokenInput) {
  return apiRequest<ShareTokenResponse>(`/properties/${id}/share-tokens`, {
    method: "POST",
    body,
  });
}

export function useIssueShareToken(propertyId: number) {
  return useMutation({
    mutationFn: (input: IssueShareTokenInput = {}) =>
      postShareToken(propertyId, input),
  });
}

// ---------------------------------------------------------------------------
// Property images (week 8 batch 13)
// ---------------------------------------------------------------------------

async function fetchPropertyImages(propertyId: number): Promise<PropertyImage[]> {
  const wires = await apiRequest<PropertyImageWire[]>(
    `/properties/${propertyId}/images`,
  );
  return wires.map(toPropertyImage);
}

function deletePropertyImage(propertyId: number, imageId: number) {
  return apiRequest<void>(
    `/properties/${propertyId}/images/${imageId}`,
    { method: "DELETE" },
  );
}

async function reorderPropertyImages(
  propertyId: number,
  orderedIds: number[],
): Promise<PropertyImage[]> {
  const wires = await apiRequest<PropertyImageWire[]>(
    `/properties/${propertyId}/images/order`,
    { method: "PATCH", body: { orderedIds } },
  );
  return wires.map(toPropertyImage);
}

export function usePropertyImages(propertyId: number | undefined) {
  return useQuery({
    queryKey:
      propertyId !== undefined
        ? propertyImageKeys.list(propertyId)
        : propertyImageKeys.lists(),
    queryFn: () => {
      if (propertyId === undefined) {
        throw new Error("usePropertyImages called without a propertyId");
      }
      return fetchPropertyImages(propertyId);
    },
    enabled: propertyId !== undefined,
  });
}

export interface UploadProgressTick {
  file: File;
  percent: number;
  status: "pending" | "uploading" | "success" | "error";
  error?: unknown | undefined;
  uploadedImage?: PropertyImage | undefined;
}

export interface UseUploadPropertyImagesInput {
  files: File[];
  onPerFileProgress?: ((tick: UploadProgressTick) => void) | undefined;
}

const UPLOAD_CONCURRENCY = 3;

/**
 * Upload one or many files to {@code /properties/{id}/images}, max 3 in
 * flight, per-file progress reported via {@code onPerFileProgress}. The
 * mutation resolves to the array of successfully uploaded images; the
 * caller is expected to also inspect the per-file ticks to render failures.
 */
export function useUploadPropertyImages(propertyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UseUploadPropertyImagesInput) => {
      // Read the token at submit time so a recently-refreshed token is
      // honoured. (XHR can't use the apiRequest refresh interceptor.)
      const token = useAuthStore.getState().accessToken;
      const url = `/api/v1/properties/${propertyId}/images`;

      // Emit initial pending ticks so the UI can render placeholders even
      // before any worker starts.
      for (const file of input.files) {
        input.onPerFileProgress?.({ file, percent: 0, status: "pending" });
      }

      const results = await withConcurrency(
        input.files,
        UPLOAD_CONCURRENCY,
        async (file) => {
          input.onPerFileProgress?.({ file, percent: 0, status: "uploading" });
          try {
            const response = await uploadFileWithProgress<PropertyImageWire[]>(
              url,
              file,
              {
                field: "files",
                authToken: token,
                onProgress: ({ percent }) =>
                  input.onPerFileProgress?.({
                    file,
                    percent,
                    status: "uploading",
                  }),
              },
            );
            // Backend returns an array (the new images, in display order).
            // Single-file uploads land at index 0.
            const firstWire = Array.isArray(response) ? response[0] : undefined;
            const uploadedImage =
              firstWire !== undefined ? toPropertyImage(firstWire) : undefined;
            input.onPerFileProgress?.({
              file,
              percent: 100,
              status: "success",
              uploadedImage,
            });
            return uploadedImage;
          } catch (error) {
            input.onPerFileProgress?.({ file, percent: 0, status: "error", error });
            throw error;
          }
        },
      );

      const successes = results
        .filter(
          (r): r is { item: File; index: number; ok: true; value: PropertyImage | undefined } =>
            r.ok,
        )
        .map((r) => r.value)
        .filter((v): v is PropertyImage => v !== undefined);
      return successes;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: propertyImageKeys.list(propertyId) });
      // Parent property thumbnailUrl + imageCount derive from the image
      // list; bust its cache too.
      void queryClient.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) });
    },
  });
}

export function useDeletePropertyImage(propertyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (imageId: number) => deletePropertyImage(propertyId, imageId),
    onMutate: async (imageId) => {
      await queryClient.cancelQueries({ queryKey: propertyImageKeys.list(propertyId) });
      const previous = queryClient.getQueryData<PropertyImage[]>(
        propertyImageKeys.list(propertyId),
      );
      if (previous !== undefined) {
        queryClient.setQueryData<PropertyImage[]>(
          propertyImageKeys.list(propertyId),
          previous.filter((img) => img.id !== imageId),
        );
      }
      return { previous };
    },
    onError: (_error, _imageId, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(propertyImageKeys.list(propertyId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: propertyImageKeys.list(propertyId) });
      void queryClient.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) });
    },
  });
}

export function useReorderPropertyImages(propertyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderedIds: number[]) => reorderPropertyImages(propertyId, orderedIds),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: propertyImageKeys.list(propertyId) });
      const previous = queryClient.getQueryData<PropertyImage[]>(
        propertyImageKeys.list(propertyId),
      );
      if (previous !== undefined) {
        // Apply the new order locally; re-derive displayOrder so the badge
        // stays in sync until the server confirms.
        const byId = new Map(previous.map((img) => [img.id, img] as const));
        const optimistic: PropertyImage[] = orderedIds.flatMap((id, idx) => {
          const img = byId.get(id);
          return img ? [{ ...img, displayOrder: idx }] : [];
        });
        queryClient.setQueryData<PropertyImage[]>(
          propertyImageKeys.list(propertyId),
          optimistic,
        );
      }
      return { previous };
    },
    onError: (_error, _orderedIds, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(propertyImageKeys.list(propertyId), context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: propertyImageKeys.list(propertyId) });
      void queryClient.invalidateQueries({ queryKey: propertyKeys.detail(propertyId) });
    },
  });
}
